export class Calculator {
  // ─── Limits ──────────────────────────────────────────────────────────────────

  private static readonly MAX_DIGITS = 25;
  private static readonly MAX_TOTAL = Calculator.MAX_DIGITS * 2 + 2; // 52
  private static readonly MAX_PROD = Calculator.MAX_TOTAL * 2; // 104

  // ─── ASCII codes ─────────────────────────────────────────────────────────────

  public static readonly C0 = 48; // '0'
  public static readonly CDOT = 46; // '.'
  private static readonly CMINUS = 45; // '-'

  // ─── Reusable typed-array buffers (allocated ONCE at class load) ──────────────

  private static readonly _aBuf = new Uint8Array(Calculator.MAX_TOTAL);
  private static readonly _bBuf = new Uint8Array(Calculator.MAX_TOTAL);
  private static readonly _mA = new Uint8Array(Calculator.MAX_TOTAL);
  private static readonly _mB = new Uint8Array(Calculator.MAX_TOTAL);
  private static readonly _rBuf = new Uint8Array(Calculator.MAX_PROD + 1); // +1 carry slot
  private static readonly _acc = new Int32Array(Calculator.MAX_PROD);
  public static readonly _out = new Uint8Array(Calculator.MAX_PROD + 3); // sign + dot

  public static readonly _td = new TextDecoder();

  // ─── Inline parse state (avoids per-call object allocation) ──────────────────

  private static _neg = false;
  private static _start = 0;
  private static _dotPos = 0;
  private static _end = 0;

  // ─── Parse ───────────────────────────────────────────────────────────────────

  private static parseInto(raw: string): void {
    const { CMINUS, CDOT } = Calculator;
    Calculator._neg = raw.charCodeAt(0) === CMINUS;
    Calculator._start = Calculator._neg ? 1 : 0;
    Calculator._end = raw.length;
    Calculator._dotPos = Calculator._end; // sentinel = no dot
    for (let i = Calculator._start; i < Calculator._end; i++) {
      if (raw.charCodeAt(i) === CDOT) {
        Calculator._dotPos = i;
        break;
      }
    }
  }

  // ─── Flatten ─────────────────────────────────────────────────────────────────

  /**
   * Write [intPart][fracPart] of raw into dst[0..iLen+fLen-1] as digit values.
   * No dot in output. Returns total length written.
   */
  private static flattenInto(
    dst: Uint8Array,
    raw: string,
    iStart: number,
    iLen: number,
    fStart: number,
    fLen: number,
    totalFLen: number,
  ): number {
    const { C0 } = Calculator;

    for (let i = 0; i < iLen; i++) {
      dst[i] = raw.charCodeAt(iStart + i) - C0;
    }

    for (let i = 0; i < fLen; i++) {
      dst[iLen + i] = raw.charCodeAt(fStart + i) - C0;
    }

    for (let i = fLen; i < totalFLen; i++) {
      dst[iLen + i] = 0;
    }

    return iLen + totalFLen;
  }

  // ─── Format ──────────────────────────────────────────────────────────────────

  /**
   * Serialise buf[from..from+total-1] to a decimal string.
   * The rightmost `fracLen` digits become the fractional part.
   * Trailing fractional zeros are trimmed. Sign prepended if neg.
   * Uses TextDecoder.decode(subarray) — one native call, zero extra alloc.
   */
  private static formatResult(buf: Uint8Array, from: number, total: number, fracLen: number, neg: boolean): string {
    const { C0, CDOT, CMINUS, _out, _td } = Calculator;
    const intLen = total - fracLen;

    // Trim trailing fractional zeros
    let fracEnd = fracLen;
    while (fracEnd > 0 && buf[from + intLen + fracEnd - 1] === 0) {
      fracEnd--;
    }

    let wi = 0;
    if (neg) {
      _out[wi++] = CMINUS;
    }

    if (intLen <= 0) {
      _out[wi++] = C0;
    } else {
      for (let i = 0; i < intLen; i++) {
        _out[wi++] = buf[from + i] + C0;
      }
    }

    if (fracEnd > 0) {
      _out[wi++] = CDOT;
      for (let i = 0; i < fracEnd; i++) {
        _out[wi++] = buf[from + intLen + i] + C0;
      }
    }

    return _td.decode(_out.subarray(0, wi));
  }

  // ─── Addition ─────────────────────────────────────────────────────────────────

  /**
   * Add _aBuf + _bBuf (already flattened) into _rBuf.
   * _rBuf layout: [carry][maxIntLen int digits][fracLen frac digits]
   * Returns [from, total] — the meaningful slice of _rBuf.
   */
  private static addFlatFull(
    aILen: number,
    aFLen: number,
    bILen: number,
    bFLen: number,
    fracLen: number,
    maxIntLen: number,
  ): [number, number] {
    const aBuf = Calculator._aBuf,
      bBuf = Calculator._bBuf,
      rBuf = Calculator._rBuf;
    const rLen = 1 + maxIntLen + fracLen;
    let carry = 0;

    // Phase 1: fractional part, right to left (shorter side implicitly zero-padded)
    for (let pos = rLen - 1; pos >= 1 + maxIntLen; pos--) {
      const fi = rLen - 1 - pos;
      const fracPos = fracLen - 1 - fi;
      const aD = fracPos < aFLen ? aBuf[aILen + fracPos] : 0;
      const bD = fracPos < bFLen ? bBuf[bILen + fracPos] : 0;
      const s = aD + bD + carry;

      carry = +(s >= 10);
      rBuf[pos] = s - carry * 10;
    }

    // Phase 2: integer part, right to left
    for (let pos = maxIntLen; pos >= 1; pos--) {
      const ii = maxIntLen - pos;
      const aD = ii < aILen ? aBuf[aILen - 1 - ii] : 0;
      const bD = ii < bILen ? bBuf[bILen - 1 - ii] : 0;
      const s = aD + bD + carry;

      carry = +(s >= 10);
      rBuf[pos] = s - carry * 10;
    }

    rBuf[0] = carry;
    const from = carry ? 0 : 1;

    return [from, rLen - from];
  }

  // ─── Subtraction ─────────────────────────────────────────────────────────────

  /**
   * Subtract _bBuf from _aBuf (|a| >= |b| guaranteed) into _rBuf.
   * Returns [from, total].
   */
  private static subtractFlatFull(
    aILen: number,
    aFLen: number,
    bILen: number,
    bFLen: number,
    fracLen: number,
    maxIntLen: number,
  ): [number, number] {
    const aBuf = Calculator._aBuf,
      bBuf = Calculator._bBuf,
      rBuf = Calculator._rBuf;
    const rLen = maxIntLen + fracLen;
    let borrow = 0;

    // Phase 1: frac, right to left
    for (let pos = rLen - 1; pos >= maxIntLen; pos--) {
      const fi = rLen - 1 - pos;
      const fracPos = fracLen - 1 - fi;

      const aD = aBuf[aILen + fracPos];
      const bD = bBuf[bILen + fracPos];

      const d = aD - bD - borrow;

      borrow = +(d < 0);
      rBuf[pos] = d + borrow * 10;
    }

    // Phase 2: int, right to left
    for (let pos = maxIntLen - 1; pos >= 0; pos--) {
      const ii = maxIntLen - 1 - pos;
      const aD = ii < aILen ? aBuf[aILen - 1 - ii] : 0;
      const bD = ii < bILen ? bBuf[bILen - 1 - ii] : 0;
      const d = aD - bD - borrow;

      borrow = +(d < 0);
      rBuf[pos] = d + borrow * 10;
    }

    let first = 0;
    while (first < rLen - 1 && rBuf[first] === 0) {
      first++;
    }

    return [first, rLen - first];
  }

  // ─── Comparison ──────────────────────────────────────────────────────────────

  /**
   * Compare |a| vs |b| via flattened buffers. Returns 1, -1, or 0.
   * Zero allocation.
   */
  private static compareFlat(aILen: number, aFLen: number, bILen: number, bFLen: number, fracLen: number): number {
    const aBuf = Calculator._aBuf,
      bBuf = Calculator._bBuf;

    let ai = 0;
    while (ai < aILen && aBuf[ai] === 0) {
      ai++;
    }

    let bi = 0;
    while (bi < bILen && bBuf[bi] === 0) {
      bi++;
    }

    const aEff = aILen - ai,
      bEff = bILen - bi;
    if (aEff !== bEff) {
      return aEff > bEff ? 1 : -1;
    }

    for (let i = 0; i < aEff; i++) {
      const d = aBuf[ai + i] - bBuf[bi + i];
      if (d !== 0) {
        return d > 0 ? 1 : -1;
      }
    }
    for (let i = 0; i < fracLen; i++) {
      const aD = i < aFLen ? aBuf[aILen + i] : 0;
      const bD = i < bFLen ? bBuf[bILen + i] : 0;
      if (aD !== bD) {
        return aD > bD ? 1 : -1;
      }
    }
    return 0;
  }

  // ─── Public: sum ─────────────────────────────────────────────────────────────

  public static sum(aRaw: string, bRaw: string): string {
    Calculator.parseInto(aRaw);
    const aNeg = Calculator._neg,
      aIS = Calculator._start,
      aDot = Calculator._dotPos,
      aE = Calculator._end;
    const aILen = aDot - aIS;
    const aFLen = aDot < aE ? aE - aDot - 1 : 0;
    const aFS = aDot < aE ? aDot + 1 : aE;

    Calculator.parseInto(bRaw);
    const bNeg = Calculator._neg,
      bIS = Calculator._start,
      bDot = Calculator._dotPos,
      bE = Calculator._end;
    const bILen = bDot - bIS;
    const bFLen = bDot < bE ? bE - bDot - 1 : 0;
    const bFS = bDot < bE ? bDot + 1 : bE;

    const fracLen = aFLen > bFLen ? aFLen : bFLen;
    const maxIntLen = aILen > bILen ? aILen : bILen;

    Calculator.flattenInto(Calculator._aBuf, aRaw, aIS, aILen, aFS, aFLen, fracLen);
    Calculator.flattenInto(Calculator._bBuf, bRaw, bIS, bILen, bFS, bFLen, fracLen);

    if (aNeg === bNeg) {
      const [from, total] = Calculator.addFlatFull(aILen, aFLen, bILen, bFLen, fracLen, maxIntLen);
      return Calculator.formatResult(Calculator._rBuf, from, total, fracLen, aNeg);
    }

    const cmp = Calculator.compareFlat(aILen, aFLen, bILen, bFLen, fracLen);
    if (cmp === 0) {
      return "0";
    }

    let from: number, total: number, resultNeg: boolean;
    if (cmp > 0) {
      [from, total] = Calculator.subtractFlatFull(aILen, aFLen, bILen, bFLen, fracLen, maxIntLen);
      resultNeg = aNeg;
    } else {
      Calculator.flattenInto(Calculator._aBuf, bRaw, bIS, bILen, bFS, bFLen, fracLen);
      Calculator.flattenInto(Calculator._bBuf, aRaw, aIS, aILen, aFS, aFLen, fracLen);
      [from, total] = Calculator.subtractFlatFull(bILen, bFLen, aILen, aFLen, fracLen, maxIntLen);
      resultNeg = bNeg;
    }

    return Calculator.formatResult(Calculator._rBuf, from, total, fracLen, resultNeg);
  }

  public static min(aRaw: string, bRaw: string): string {
    return Calculator.sum(aRaw, `-${bRaw}`);
  }

  // ─── Multiply flat ───────────────────────────────────────────────────────────

  /**
   * Multiply _mA[0..nA-1] × _mB[0..nB-1] into _rBuf.
   *
   * Strategy: accumulate all products into _acc (Int32Array) WITHOUT carry,
   * then do one carry-normalisation pass. This keeps the O(n²) inner loop
   * completely branch-free and register-friendly.
   *
   * Max _acc cell value before normalisation: 9×9×(MAX_TOTAL) ≤ 9×9×52 = 4212
   * — comfortably within Int32 range.
   */
  private static multiplyFlat(nA: number, nB: number): void {
    // Hoist into locals — eliminates `this.`/`Calculator.` lookup per inner iteration
    const mA = Calculator._mA,
      mB = Calculator._mB;
    const acc = Calculator._acc,
      rBuf = Calculator._rBuf;
    const outLen = nA + nB;

    for (let k = 0; k < outLen; k++) {
      acc[k] = 0;
    }

    for (let i = nA - 1; i >= 0; i--) {
      const a = mA[i];

      if (a === 0) {
        continue;
      }

      for (let j = nB - 1; j >= 0; j--) {
        acc[i + j + 1] += a * mB[j];
      }
    }

    let carry = 0;
    for (let k = outLen - 1; k >= 0; k--) {
      const v = acc[k] + carry;
      carry = (v / 10) | 0;
      rBuf[k] = v - carry * 10;
    }
  }

  // ─── Public: mult ────────────────────────────────────────────────────────────

  public static mult(aRaw: string, bRaw: string): string {
    Calculator.parseInto(aRaw);
    const aNeg = Calculator._neg,
      aIS = Calculator._start,
      aDot = Calculator._dotPos,
      aE = Calculator._end;
    const aILen = aDot - aIS;
    const aFLen = aDot < aE ? aE - aDot - 1 : 0;
    const aFS = aDot < aE ? aDot + 1 : aE;

    Calculator.parseInto(bRaw);
    const bNeg = Calculator._neg,
      bIS = Calculator._start,
      bDot = Calculator._dotPos,
      bE = Calculator._end;
    const bILen = bDot - bIS;
    const bFLen = bDot < bE ? bE - bDot - 1 : 0;
    const bFS = bDot < bE ? bDot + 1 : bE;

    const fracDigits = aFLen + bFLen;

    let aIsZero = true;
    for (let i = aIS; i < aE; i++) {
      const c = aRaw.charCodeAt(i);
      if (c !== Calculator.C0 && c !== Calculator.CDOT) {
        aIsZero = false;
        break;
      }
    }
    if (aIsZero) {
      return "0";
    }

    let bIsZero = true;
    for (let i = bIS; i < bE; i++) {
      const c = bRaw.charCodeAt(i);
      if (c !== Calculator.C0 && c !== Calculator.CDOT) {
        bIsZero = false;
        break;
      }
    }
    if (bIsZero) {
      return "0";
    }

    const nA = Calculator.flattenInto(Calculator._mA, aRaw, aIS, aILen, aFS, aFLen, aFLen);
    const nB = Calculator.flattenInto(Calculator._mB, bRaw, bIS, bILen, bFS, bFLen, bFLen);

    Calculator.multiplyFlat(nA, nB);

    const outLen = nA + nB;
    let first = 0;
    while (first < outLen - fracDigits - 1 && Calculator._rBuf[first] === 0) {
      first++;
    }

    return Calculator.formatResult(Calculator._rBuf, first, outLen - first, fracDigits, aNeg !== bNeg);
  }

  // ─── Divide ─────────────────────────────────────────────────────────────

  public static div(aRaw: string, bRaw: string): string {
    Calculator.parseInto(aRaw);
    const aNeg = Calculator._neg,
      aIS = Calculator._start,
      aDot = Calculator._dotPos,
      aE = Calculator._end;
    const aILen = aDot - aIS;
    const aFLen = aDot < aE ? aE - aDot - 1 : 0;
    const aFS = aDot < aE ? aDot + 1 : aE;

    Calculator.parseInto(bRaw);
    const bNeg = Calculator._neg,
      bIS = Calculator._start,
      bDot = Calculator._dotPos,
      bE = Calculator._end;
    const bILen = bDot - bIS;
    const bFLen = bDot < bE ? bE - bDot - 1 : 0;
    const bFS = bDot < bE ? bDot + 1 : bE;

    const nA = Calculator.flattenInto(Calculator._mA, aRaw, aIS, aILen, aFS, aFLen, aFLen);
    const nB = Calculator.flattenInto(Calculator._mB, bRaw, bIS, bILen, bFS, bFLen, bFLen);

    const mA = Calculator._mA,
      mB = Calculator._mB,
      rBuf = Calculator._rBuf;

    let divZero = true;
    for (let i = 0; i < nB; i++) {
      if (mB[i] !== 0) {
        divZero = false;
        break;
      }
    }

    if (divZero) {
      throw new Error("Division by zero");
    }

    let numZero = true;
    for (let i = 0; i < nA; i++) {
      if (mA[i] !== 0) {
        numZero = false;
        break;
      }
    }

    if (numZero) {
      return "0";
    }

    const PREC = Calculator.MAX_DIGITS;
    const scale = aFLen > bFLen ? aFLen - bFLen : bFLen - aFLen;
    const rem = Calculator._acc;
    const pBuf = Calculator._acc;
    const pBase = nB + 1;

    for (let i = 0; i < nB; i++) {
      rem[i] = 0;
    }

    let remLen = 0;
    let qPos = 0;
    const totalSteps = nA + PREC;

    for (let step = 0; step < totalSteps; step++) {
      rem[remLen++] = step < nA ? mA[step] : 0;

      // trim leading zeros
      let rs = 0;
      while (rs < remLen - 1 && rem[rs] === 0) {
        rs++;
      }

      if (rs > 0) {
        for (let i = rs; i < remLen; i++) {
          rem[i - rs] = rem[i];
        }
        remLen -= rs;
      }

      // binary search for quotient digit
      let lo = 0,
        hi = 9;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        let carry = 0;

        for (let i = nB - 1; i >= 0; i--) {
          const v = mid * mB[i] + carry;
          carry = (v / 10) | 0;
          pBuf[pBase + i + 1] = v - carry * 10;
        }

        pBuf[pBase] = carry;
        const pLen = carry ? nB + 1 : nB;
        const pStart = carry ? pBase : pBase + 1;

        let remCmp = remLen > pLen ? 1 : remLen < pLen ? -1 : 0;
        if (remCmp === 0) {
          for (let i = 0; i < remLen; i++) {
            const d = rem[i] - pBuf[pStart + i];
            if (d !== 0) {
              remCmp = d > 0 ? 1 : -1;
              break;
            }
          }
        }

        if (remCmp >= 0) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      rBuf[qPos++] = lo;

      // subtract lo * mB from rem
      if (lo > 0) {
        let carry = 0;
        for (let i = nB - 1; i >= 0; i--) {
          const v = lo * mB[i] + carry;
          carry = (v / 10) | 0;
          pBuf[pBase + i + 1] = v - carry * 10;
        }
        pBuf[pBase] = carry;
        const pLen = carry ? nB + 1 : nB;
        const pStart = carry ? pBase : pBase + 1;

        let borrow = 0;
        for (let i = remLen - 1; i >= 0; i--) {
          const pIdx = pLen - remLen + i;
          const p = pIdx >= 0 ? pBuf[pStart + pIdx] : 0;
          let d = rem[i] - p - borrow;
          if (d < 0) {
            d += 10;
            borrow = 1;
          } else {
            borrow = 0;
          }
          rem[i] = d;
        }

        let trim = 0;
        while (trim < remLen - 1 && rem[trim] === 0) trim++;
        if (trim > 0) {
          for (let i = trim; i < remLen; i++) rem[i - trim] = rem[i];
          remLen -= trim;
        }
      }
    }

    let fracLen = PREC + scale;
    if (fracLen < 0) {
      fracLen = 0;
    }
    if (fracLen > qPos) {
      fracLen = qPos;
    }

    let first = 0;
    while (first < qPos - 1 && rBuf[first] === 0) {
      first++;
    }

    return Calculator.formatResult(rBuf, first, qPos - first, fracLen, aNeg !== bNeg);
  }

  // ── Derive exponent of this.min purely from the string ────────────────────
  // e.g. "0.1"  → exponent -1  → magnitude = 10^(-1+1) = 10^0 = "1"
  //      "0.01" → exponent -2  → magnitude = 10^(-2+1) = 10^-1 = "0.1"
  //      "1"    → exponent  0  → magnitude = 10^(0+1)  = 10^1  = "10"
  //      "10"   → exponent  1  → magnitude = 10^(1+1)  = 10^2  = "100"
  public static exp(min: string): number {
    const { C0 } = Calculator;
    const dotIdx = min.indexOf(".");
    if (dotIdx === -1) {
      let exp = 0;
      for (let i = min.length - 1; i >= 0; i--) {
        if (min.charCodeAt(i) !== C0) {
          break;
        }
        exp++;
      }
      return min.length - 1 - exp;
    }

    let exp = -1;
    for (let i = dotIdx + 1; i < min.length; i++) {
      if (min.charCodeAt(i) !== C0) {
        break;
      }
      exp--;
    }
    return exp;
  }

  /**
   * Generate a pure-string power of 10 for a given exponent.
   * powerOfTen(0)  → "1"
   * powerOfTen(1)  → "10"
   * powerOfTen(-1) → "0.1"
   * powerOfTen(-2) → "0.01"
   */
  public static pow(exp: number): string {
    if (exp >= 0) {
      return `1${"0".repeat(exp)}`;
    }

    return `0.${"0".repeat(-exp - 1)}1`;
  }

  // ─── Public: comparisons ─────────────────────────────────────────────────────

  /**
   * Core signed comparison. Returns 1, 0, or -1.
   *
   * Sign rules applied before delegating to the absolute-value compareFlat:
   *   (+) vs (−) → always 1
   *   (−) vs (+) → always -1
   *   both (−)   → absolute comparison is INVERTED  (−3 < −1 even though |3|>|1|)
   *   both (+)   → absolute comparison as-is
   *
   * Reuses the existing parse/validate/flatten pipeline — zero extra allocation.
   */
  private static compare(aRaw: string, bRaw: string): number {
    Calculator.parseInto(aRaw);
    const aNeg = Calculator._neg,
      aIS = Calculator._start,
      aDot = Calculator._dotPos,
      aE = Calculator._end;
    const aILen = aDot - aIS;
    const aFLen = aDot < aE ? aE - aDot - 1 : 0;
    const aFS = aDot < aE ? aDot + 1 : aE;

    Calculator.parseInto(bRaw);
    const bNeg = Calculator._neg,
      bIS = Calculator._start,
      bDot = Calculator._dotPos,
      bE = Calculator._end;
    const bILen = bDot - bIS;
    const bFLen = bDot < bE ? bE - bDot - 1 : 0;
    const bFS = bDot < bE ? bDot + 1 : bE;

    if (!aNeg && bNeg) {
      return 1;
    }
    if (aNeg && !bNeg) {
      return -1;
    }

    const fracLen = aFLen > bFLen ? aFLen : bFLen;

    Calculator.flattenInto(Calculator._aBuf, aRaw, aIS, aILen, aFS, aFLen, fracLen);
    Calculator.flattenInto(Calculator._bBuf, bRaw, bIS, bILen, bFS, bFLen, fracLen);

    const abs = Calculator.compareFlat(aILen, aFLen, bILen, bFLen, fracLen);

    return aNeg ? -abs : abs;
  }

  /** Returns true if a === b */
  public static eq(aRaw: string, bRaw: string): boolean {
    if (aRaw === bRaw) {
      return true;
    }

    return Calculator.compare(aRaw, bRaw) === 0;
  }

  /** Returns true if a > b */
  public static gt(aRaw: string, bRaw: string): boolean {
    return Calculator.compare(aRaw, bRaw) === 1;
  }

  /** Returns true if a >= b */
  public static gte(aRaw: string, bRaw: string): boolean {
    return Calculator.compare(aRaw, bRaw) >= 0;
  }

  /** Returns true if a < b */
  public static lt(aRaw: string, bRaw: string): boolean {
    return Calculator.compare(aRaw, bRaw) === -1;
  }

  /** Returns true if a <= b */
  public static lte(aRaw: string, bRaw: string): boolean {
    return Calculator.compare(aRaw, bRaw) <= 0;
  }
}
